import React, { useState, useRef, useEffect } from 'react';
import { useBudget } from './BudgetContext';
import { FaChevronDown, FaLock, FaUser } from 'react-icons/fa';
import styles from './BudgetSwitcher.module.css';

/**
 * BudgetSwitcher - Dropdown component to switch between own budget and shared budgets
 */
export const BudgetSwitcher: React.FC = () => {
  const {
    viewingUserId,
    viewingUserName,
    sharedBudgets,
    setViewingUser,
    isReadOnly,
  } = useBudget();
  
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // If no shared budgets, don't show the switcher
  if (sharedBudgets.length === 0 && !isReadOnly) {
    return null;
  }
  
  const currentLabel = viewingUserId 
    ? viewingUserName || 'Shared Budget'
    : 'My Budget';
  
  const handleSelect = (userId: string | null, userName: string | null) => {
    setViewingUser(userId, userName);
    setIsOpen(false);
  };
  
  return (
    <div className={styles.container} ref={dropdownRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {isReadOnly && FaLock({ className: styles.lockIcon })}
        <span className={styles.label}>{currentLabel}</span>
        {FaChevronDown({ className: `${styles.chevron} ${isOpen ? styles.chevronOpen : ''}` })}
      </button>
      
      {isOpen && (
        <div className={styles.dropdown} role="listbox">
          {/* Own budget option */}
          <button
            type="button"
            className={`${styles.option} ${!viewingUserId ? styles.optionSelected : ''}`}
            onClick={() => handleSelect(null, null)}
            role="option"
            aria-selected={!viewingUserId}
          >
            {FaUser({ className: styles.optionIcon })}
            <span>My Budget</span>
            {!viewingUserId && <span className={styles.checkmark}>✓</span>}
          </button>
          
          {/* Divider if there are shared budgets */}
          {sharedBudgets.length > 0 && <div className={styles.divider} />}
          
          {/* Shared budget options */}
          {sharedBudgets.map((share) => {
            const displayName = share.owner.displayName || share.owner.email;
            const isSelected = viewingUserId === share.owner.id;
            
            return (
              <button
                key={share.id}
                type="button"
                className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
                onClick={() => handleSelect(share.owner.id, displayName)}
                role="option"
                aria-selected={isSelected}
              >
                {FaLock({ className: styles.optionIcon })}
                <span>{displayName}'s Budget</span>
                {isSelected && <span className={styles.checkmark}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BudgetSwitcher;
